import { BigNumber } from 'ethers'

import { uniqueTokenIds } from './marketplaceModel'

const IPFS_GATEWAY = 'https://ipfs.gateway.fatsale.org/ipfs/'
const IPFS_UPLOAD_ENDPOINT = 'https://ipfs.fatsale.org/api/v0/add?pin=true'

async function uploadIpfsFile(file, filename) {
    const body = new FormData()
    body.append('file', file, filename)
    const response = await fetch(IPFS_UPLOAD_ENDPOINT, { method: 'POST', body })
    if (!response.ok) throw new Error(`IPFS upload failed with HTTP ${response.status}`)
    const payload = (await response.text()).trim().split('\n').filter(Boolean).map((line) => JSON.parse(line)).pop()
    const hash = payload?.Hash || payload?.hash || payload?.path
    if (!hash) throw new Error('IPFS upload did not return a content hash')
    return `ipfs://${hash}`
}

export async function uploadMarketplaceMetadata(metadata) {
    return uploadIpfsFile(
        new Blob([JSON.stringify(metadata)], { type: 'application/json' }),
        'marketplace.json'
    )
}

export async function uploadMarketplaceImage(file) {
    return uploadIpfsFile(file, file.name || 'market-logo')
}

export function normalizeMetadataUrl(url) {
    if (!url) return ''
    if (url.startsWith('ipfs://')) return `${IPFS_GATEWAY}${url.slice(7)}`
    return url
}

export async function fetchMetadata(url, { signal } = {}) {
    if (!url) return {}
    const response = await fetch(normalizeMetadataUrl(url), { signal })
    if (!response.ok) throw new Error(`Metadata request failed with HTTP ${response.status}`)
    const metadata = await response.json()
    return {
        ...metadata,
        logo: normalizeMetadataUrl(metadata.logo || ''),
        image: normalizeMetadataUrl(metadata.image || ''),
        animation_url: normalizeMetadataUrl(metadata.animation_url || ''),
    }
}

export async function mapWithConcurrency(items, limit, mapper) {
    const results = new Array(items.length)
    let nextIndex = 0

    async function worker() {
        while (nextIndex < items.length) {
            const index = nextIndex
            nextIndex += 1
            results[index] = await mapper(items[index], index)
        }
    }

    const workerCount = Math.min(Math.max(1, limit), items.length)
    await Promise.all(Array.from({ length: workerCount }, worker))
    return results
}

export async function loadTokenMetadataPage(contract, tokenIds, options = {}) {
    const signal = options.signal
    return mapWithConcurrency(tokenIds, options.concurrency || 4, async (tokenId) => {
        const id = BigNumber.from(tokenId).toString()
        try {
            const uri = await contract.tokenURI(tokenId)
            const metadata = await fetchMetadata(uri, { signal })
            return { id, uri, metadata, error: '' }
        } catch (error) {
            return {
                id,
                uri: '',
                metadata: {},
                error: error?.message || 'Unable to load NFT metadata',
            }
        }
    })
}

export async function loadMarketplaceOwnedTokenIds(contract, owner, options = {}) {
    const chunkSize = BigNumber.from(options.chunkSize || 500)
    try {
        const tokenIds = await contract.tokensOfOwner(owner)
        if (!Array.isArray(tokenIds)) throw new Error('tokensOfOwner returned an invalid result')
        return { tokenIds: uniqueTokenIds(tokenIds), strategy: 'all' }
    } catch (fastPathError) {
        const balance = BigNumber.from(await contract.balanceOf(owner))
        if (balance.isZero()) return { tokenIds: [], strategy: 'empty', fastPathError }

        try {
            const tokenIds = []
            for (let index = 0; index < balance.toNumber(); index += 1) {
                tokenIds.push(await contract.tokenOfOwnerByIndex(owner, index))
            }
            return {
                tokenIds: uniqueTokenIds(tokenIds),
                strategy: 'enumerable',
                fastPathError,
            }
        } catch (enumerableError) {
            const totalSupply = BigNumber.from(await contract.totalSupply())
            const stopExclusive = totalSupply.add(1)
            const tokenIds = []
            let start = BigNumber.from(1)

            while (start.lt(stopExclusive) && BigNumber.from(tokenIds.length).lt(balance)) {
                const candidateStop = start.add(chunkSize)
                const stop = candidateStop.lt(stopExclusive) ? candidateStop : stopExclusive
                const range = await contract.tokensOfOwnerIn(owner, start, stop)
                if (!Array.isArray(range)) throw new Error('tokensOfOwnerIn returned an invalid result')
                tokenIds.push(...range)
                start = stop
            }

            const uniqueIds = uniqueTokenIds(tokenIds)
            if (!BigNumber.from(uniqueIds.length).eq(balance)) {
                const error = new Error(
                    `Owned token scan incomplete: expected ${balance.toString()}, received ${uniqueIds.length}`
                )
                error.fastPathError = fastPathError
                error.enumerableError = enumerableError
                throw error
            }
            return {
                tokenIds: uniqueIds,
                strategy: 'ranges',
                fastPathError,
                enumerableError,
            }
        }
    }
}

export function listingFromResult(listing) {
    return {
        listingId: BigNumber.from(listing.listingId ?? listing[0]),
        sourceListingId: BigNumber.from(listing.sourceListingId ?? listing[1]),
        tokenId: BigNumber.from(listing.tokenId ?? listing[2]),
        seller: listing.seller ?? listing[3],
        currency: listing.currency ?? listing[4],
        price: BigNumber.from(listing.price ?? listing[5]),
        expiresAt: Number(listing.expiresAt ?? listing[6]),
        projectFeeBps: Number(listing.projectFeeBps ?? listing[7]),
        platformFeeBps: Number(listing.platformFeeBps ?? listing[8]),
        royaltyBps: Number(listing.royaltyBps ?? listing[9]),
        royaltyMode: Number(listing.royaltyMode ?? listing[10]),
        projectFeeRecipient: listing.projectFeeRecipient ?? listing[11],
        platformFeeRecipient: listing.platformFeeRecipient ?? listing[12],
        royaltyRecipient: listing.royaltyRecipient ?? listing[13],
        projectFeeAmount: BigNumber.from(listing.projectFeeAmount ?? listing[14]),
        platformFeeAmount: BigNumber.from(listing.platformFeeAmount ?? listing[15]),
        royaltyAmount: BigNumber.from(listing.royaltyAmount ?? listing[16]),
        active: Boolean(listing.active ?? listing[17]),
        migratedTo: listing.migratedTo ?? listing[18],
    }
}

export function saleFromResult(sale) {
    return {
        saleId: BigNumber.from(sale.saleId ?? sale[0]),
        listingId: BigNumber.from(sale.listingId ?? sale[1]),
        tokenId: BigNumber.from(sale.tokenId ?? sale[2]),
        seller: sale.seller ?? sale[3],
        buyer: sale.buyer ?? sale[4],
        currency: sale.currency ?? sale[5],
        price: BigNumber.from(sale.price ?? sale[6]),
        sellerProceeds: BigNumber.from(sale.sellerProceeds ?? sale[7]),
        projectFeeAmount: BigNumber.from(sale.projectFeeAmount ?? sale[8]),
        platformFeeAmount: BigNumber.from(sale.platformFeeAmount ?? sale[9]),
        royaltyAmount: BigNumber.from(sale.royaltyAmount ?? sale[10]),
        soldAt: Number(sale.soldAt ?? sale[11]),
    }
}
